import { cliente } from '@/lib/cliente';
import type {
  ActualizarM3ColadaManualRequest,
  AnularColadaRequest,
  AnularColadasBulkRequest,
  AnularColadasBulkResponse,
  AsignarObraRequest,
  BatchHuerfano,
  ColadaDetalle,
  ColadaListadoItem,
  ConsolidarColadasRequest,
  CrearColadaManualRequest,
  ExcluirBatchRequest,
  GuardarBomOverrideRequest,
  ListarColadasRequest,
  ListarObrasRequest,
  MarcarDigitadaRequest,
  Obra,
  ObtenerBomColadaResponse,
  RespuestaApi,
} from '@adelante/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

// =============================================================================
// Queries
// =============================================================================

/**
 * Lista de coladas con filtros. La key incluye todos los params para que
 * TanStack Query distinga entre combinaciones de filtros.
 */
export function useColadas(filtros: Partial<ListarColadasRequest>) {
  return useQuery<
    RespuestaApi<{
      coladas: ColadaListadoItem[];
      total: number;
      pagina: number;
      por_pagina: number;
    }>
  >({
    queryKey: ['coladas', filtros],
    queryFn: () => cliente.coladas.listar(filtros),
  });
}

/**
 * Detalle de una colada por id. `enabled: id !== null` permite que la pantalla
 * de detalle no dispare la query hasta que el parámetro de URL esté listo.
 */
export function useColada(id: number | null) {
  return useQuery<RespuestaApi<ColadaDetalle>>({
    queryKey: ['colada', id],
    queryFn: () => {
      if (id === null) throw new Error('id es null');
      return cliente.coladas.obtener(id);
    },
    enabled: id !== null,
  });
}

/**
 * BOM de una colada manual (Fiori): BC real + overrides locales de esa
 * colada. `enabled: origen_registro==='manual'` lo decide el componente que
 * llama (pasa null cuando no aplica).
 */
export function useBomColada(idColada: number | null) {
  return useQuery<RespuestaApi<ObtenerBomColadaResponse>>({
    queryKey: ['colada-bom', idColada],
    queryFn: () => cliente.coladas.obtenerBom(idColada as number),
    enabled: idColada !== null,
  });
}

/** Corrige la cantidad por unidad de un componente de la BOM, solo para esta colada. */
export function useGuardarBomOverride() {
  const qc = useQueryClient();
  return useMutation<
    RespuestaApi<ObtenerBomColadaResponse>,
    Error,
    { idColada: number; codigo: string } & GuardarBomOverrideRequest
  >({
    mutationFn: ({ idColada, codigo, ...body }) =>
      cliente.coladas.guardarBomOverride(idColada, codigo, body),
    onSuccess: (respuesta, vars) => {
      if (respuesta.data) {
        toast.success('Cantidad actualizada');
        qc.setQueryData(['colada-bom', vars.idColada], respuesta);
      } else {
        toast.error(respuesta.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => {
      toast.error('No se pudo actualizar la cantidad', { description: e.message });
    },
  });
}

/**
 * Registro manual de una colada (autocargables Fiori). A diferencia de las
 * transiciones (que operan sobre una colada existente), esto crea una
 * colada nueva — no encaja en `crearMutationTransicion`.
 */
export function useCrearColadaManual() {
  const qc = useQueryClient();
  return useMutation<RespuestaApi<ColadaDetalle>, Error, CrearColadaManualRequest>({
    mutationFn: (body) => cliente.coladas.crearManual(body),
    onSuccess: (respuesta) => {
      if (respuesta.data) {
        toast.success(`Colada #${respuesta.data.colada.codigo_interno} registrada`);
        qc.invalidateQueries({ queryKey: ['coladas'] });
      } else {
        toast.error(respuesta.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => {
      toast.error('No se pudo registrar la colada', { description: e.message });
    },
  });
}

/** Corrige los m³ de una colada manual (Fiori) ya registrada. */
export function useActualizarM3Manual() {
  const qc = useQueryClient();
  return useMutation<
    RespuestaApi<ColadaDetalle>,
    Error,
    { id: number } & ActualizarM3ColadaManualRequest
  >({
    mutationFn: ({ id, ...body }) => cliente.coladas.actualizarM3Manual(id, body),
    onSuccess: (respuesta, vars) => {
      if (respuesta.data) {
        toast.success('m³ actualizado');
        qc.setQueryData(['colada', vars.id], respuesta);
        qc.invalidateQueries({ queryKey: ['coladas'] });
        // La tabla BOM (Líneas Pedido BC) recalcula "Cantidad total" a partir
        // de estos m³, pero vive en una query separada ('colada-bom') — sin
        // esto quedaba mostrando los totales viejos hasta recargar a mano.
        qc.invalidateQueries({ queryKey: ['colada-bom', vars.id] });
      } else {
        toast.error(respuesta.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => {
      toast.error('No se pudo actualizar los m³', { description: e.message });
    },
  });
}

// =============================================================================
// Mutations — todas con el mismo patrón
// =============================================================================

/**
 * Factory de hooks de mutación de transición. Centraliza la lógica común:
 *  - Toast de éxito con `mensajeOk`.
 *  - Toast de error con el mensaje del backend (incluye 409 CONFLICTO_ESTADO).
 *  - Invalida la query del detalle y la del listado.
 *  - Pre-llena el cache del detalle con la response (cero round-trip extra).
 */
function crearMutationTransicion<TVars>(args: {
  mutationFn: (vars: TVars & { id: number }) => Promise<RespuestaApi<ColadaDetalle>>;
  mensajeOk: (vars: TVars & { id: number }) => string;
}) {
  return () => {
    const qc = useQueryClient();
    return useMutation<RespuestaApi<ColadaDetalle>, Error, TVars & { id: number }>({
      mutationFn: args.mutationFn,
      onSuccess: (respuesta, vars) => {
        if (respuesta.data) {
          toast.success(args.mensajeOk(vars));
          qc.setQueryData(['colada', vars.id], respuesta);
          qc.invalidateQueries({ queryKey: ['coladas'] });
        } else {
          toast.error(respuesta.error?.mensaje ?? 'Error desconocido');
        }
      },
      onError: (e) => {
        toast.error('No se pudo aplicar la transición', { description: e.message });
      },
    });
  };
}

// Vars vacías: usamos un tipo objeto vacío explícito, no `Record<string, never>`
// (que rompe la intersección con `{ id: number }`).
// biome-ignore lint/complexity/noBannedTypes: el objeto vacío es lo más preciso aquí.
type SinExtras = {};

export const useConfirmarColada = crearMutationTransicion<SinExtras>({
  mutationFn: ({ id }) => cliente.coladas.confirmar(id),
  mensajeOk: () => 'Colada confirmada',
});

export const useDesconfirmarColada = crearMutationTransicion<SinExtras>({
  mutationFn: ({ id }) => cliente.coladas.desconfirmar(id),
  mensajeOk: () => 'Colada vuelta a sugerida',
});

export const useMarcarDigitada = crearMutationTransicion<MarcarDigitadaRequest>({
  mutationFn: ({ id, ...body }) =>
    cliente.coladas.marcarDigitada(id, body as MarcarDigitadaRequest),
  mensajeOk: ({ numero_pedido_ensamblado_bc }) =>
    `Colada marcada digitada (${numero_pedido_ensamblado_bc})`,
});

export const useDesmarcarDigitada = crearMutationTransicion<SinExtras>({
  mutationFn: ({ id }) => cliente.coladas.desmarcarDigitada(id),
  mensajeOk: () => 'Colada vuelta a confirmada',
});

export const useCerrarColada = crearMutationTransicion<SinExtras>({
  mutationFn: ({ id }) => cliente.coladas.cerrar(id),
  mensajeOk: () => 'Colada cerrada',
});

export const useAnularColada = crearMutationTransicion<AnularColadaRequest>({
  mutationFn: ({ id, ...body }) => cliente.coladas.anular(id, body as AnularColadaRequest),
  mensajeOk: () => 'Colada anulada',
});

export const useDesanularColada = crearMutationTransicion<SinExtras>({
  mutationFn: ({ id }) => cliente.coladas.desanular(id),
  mensajeOk: () => 'Colada desanulada (vuelve a "sugerida")',
});

// =============================================================================
// Sesión 6 — Excluir / agregar batch + listar huérfanos
// =============================================================================

/**
 * Excluye un batch de su colada actual.
 *
 * El `id` del factory de transiciones es el de la **colada actual** del
 * batch (no el del batch). Pasamos `idBatch` aparte. Al éxito refresca
 * el detalle de la colada Y la lista de huérfanos.
 */
export function useExcluirBatch() {
  const qc = useQueryClient();
  return useMutation<
    RespuestaApi<ColadaDetalle>,
    Error,
    { idColada: number; idBatch: number } & ExcluirBatchRequest
  >({
    mutationFn: ({ idColada, idBatch, ...body }) =>
      cliente.coladas.excluirBatch(idColada, idBatch, body as ExcluirBatchRequest),
    onSuccess: (respuesta, vars) => {
      if (respuesta.data) {
        toast.success(`Batch ${vars.idBatch} excluido de la colada`);
        qc.setQueryData(['colada', vars.idColada], respuesta);
        qc.invalidateQueries({ queryKey: ['coladas'] });
        qc.invalidateQueries({ queryKey: ['batches-huerfanos'] });
      } else {
        toast.error(respuesta.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => {
      toast.error('No se pudo excluir el batch', { description: e.message });
    },
  });
}

/**
 * Agrega (mueve) un batch huérfano a una colada destino. Invalida el
 * detalle de la colada destino + lista de huérfanos + listado general.
 */
export function useAgregarBatch() {
  const qc = useQueryClient();
  return useMutation<
    RespuestaApi<ColadaDetalle>,
    Error,
    { idColadaDestino: number; idBatch: number }
  >({
    mutationFn: ({ idColadaDestino, idBatch }) =>
      cliente.coladas.agregarBatch(idColadaDestino, idBatch),
    onSuccess: (respuesta, vars) => {
      if (respuesta.data) {
        toast.success(`Batch ${vars.idBatch} agregado a la colada`);
        qc.setQueryData(['colada', vars.idColadaDestino], respuesta);
        qc.invalidateQueries({ queryKey: ['coladas'] });
        qc.invalidateQueries({ queryKey: ['batches-huerfanos'] });
      } else {
        toast.error(respuesta.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => {
      toast.error('No se pudo agregar el batch', { description: e.message });
    },
  });
}

/**
 * Restaura un batch huérfano a la colada de la que se había excluido.
 * Inverso de `useExcluirBatch`: el batch sigue vinculado a esa colada
 * (id_colada no cambia), solo se limpia el flag `excluido`. Es el camino
 * rápido cuando se excluyó por error.
 */
export function useRestaurarBatch() {
  const qc = useQueryClient();
  return useMutation<RespuestaApi<ColadaDetalle>, Error, { idColada: number; idBatch: number }>({
    mutationFn: ({ idColada, idBatch }) => cliente.coladas.restaurarBatch(idColada, idBatch),
    onSuccess: (respuesta, vars) => {
      if (respuesta.data) {
        toast.success(`Batch ${vars.idBatch} restaurado a su colada original`);
        qc.setQueryData(['colada', vars.idColada], respuesta);
        qc.invalidateQueries({ queryKey: ['coladas'] });
        qc.invalidateQueries({ queryKey: ['batches-huerfanos'] });
      } else {
        toast.error(respuesta.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => {
      toast.error('No se pudo restaurar el batch', { description: e.message });
    },
  });
}

/**
 * Lista de batches huérfanos (excluidos pendientes de reasignar).
 */
export function useBatchesHuerfanos() {
  return useQuery<RespuestaApi<{ huerfanos: BatchHuerfano[]; total: number }>>({
    queryKey: ['batches-huerfanos'],
    queryFn: () => cliente.batches.huerfanos(),
  });
}

// =============================================================================
// Sesión 7 — Obras (asignación a coladas)
// =============================================================================

/**
 * Lista de obras desde `bi.dim_obra`. Cache de 5 min porque las obras
 * cambian con el ETL nocturno; no hace falta refrescar en cada interacción.
 */
export function useObras(filtros: Partial<ListarObrasRequest> = {}) {
  return useQuery<RespuestaApi<{ obras: Obra[]; total: number }>>({
    queryKey: ['obras', filtros],
    queryFn: () => cliente.obras.listar(filtros),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Consolida 2+ coladas en la de id más bajo. Las absorbidas pasan a
 * 'anulada' con motivo automatico. Devuelve el detalle de la principal.
 *
 * Vars: `{ ids_a_consolidar: number[] }`.
 */
/**
 * Mutación para consolidar 2+ coladas en una.
 *
 * IMPORTANTE: a diferencia de otras mutaciones, NO mostramos toast en error.
 * El caller debe registrar `onError` en su `mutate()` para abrir un modal
 * persistente — los errores de consolidación (típicamente 409 ESTADO_INVALIDO
 * cuando una colada ya está cerrada/digitada) tienen mensaje largo y la
 * operadora necesita leerlo con calma; el toast efímero se ignora. Ver
 * `ModalErrorConsolidacion` en ColadasListaPantalla.
 */
export function useConsolidarColadas() {
  const qc = useQueryClient();
  return useMutation<RespuestaApi<ColadaDetalle>, Error, ConsolidarColadasRequest>({
    mutationFn: (body) => cliente.coladas.consolidar(body),
    onSuccess: (respuesta) => {
      if (respuesta.data) {
        const cantidad = respuesta.data.colada.cantidad_batches;
        toast.success(
          `Consolidación OK: ${cantidad} ${cantidad === 1 ? 'batch' : 'batches'} en la colada principal #${respuesta.data.colada.codigo_interno}`,
        );
        // Invalida toda la lista porque cambiaron varias coladas (anuladas + principal).
        qc.invalidateQueries({ queryKey: ['coladas'] });
        // Pre-llena el detalle de la principal por si el usuario navega.
        qc.setQueryData(['colada', respuesta.data.colada.id_colada], respuesta);
      }
      // Si respuesta.error existe, el caller lo recibe vía onSuccess pero
      // mejor lo mapeamos a onError para que el flujo del modal sea consistente.
      // En la práctica, el cliente ya tira ApiError ante !respuesta.ok, así
      // que respuesta.error casi nunca llega acá — pero queda como red de
      // seguridad por si algún endpoint devuelve 200 + error en el body.
    },
    // Sin onError: el caller maneja el error para abrir el modal persistente.
  });
}

/**
 * Anula varias coladas de una sola vez con el mismo motivo (ej. lote
 * cargado con fecha incorrecta). `eliminar_batches` además borra los
 * batches detrás — necesario para que una recarga posterior del CSV
 * corregido no los salte por dedup (planta + N° de batch).
 */
export function useAnularColadasBulk() {
  const qc = useQueryClient();
  return useMutation<RespuestaApi<AnularColadasBulkResponse>, Error, AnularColadasBulkRequest>({
    mutationFn: (body) => cliente.coladas.anularBulk(body),
    onSuccess: (respuesta) => {
      if (respuesta.data) {
        toast.success(
          `${respuesta.data.cantidad} ${respuesta.data.cantidad === 1 ? 'colada anulada' : 'coladas anuladas'}`,
        );
        qc.invalidateQueries({ queryKey: ['coladas'] });
        for (const id of respuesta.data.ids_anuladas) {
          qc.invalidateQueries({ queryKey: ['colada', id] });
        }
      } else if (respuesta.error) {
        toast.error(respuesta.error.mensaje);
      }
    },
    onError: (e) => {
      toast.error('No se pudo anular el lote', { description: e.message });
    },
  });
}

/**
 * Asigna/cambia/quita la obra de una colada. Misma estructura que
 * las mutaciones de transición: invalida detalle + lista.
 */
export function useAsignarObra() {
  const qc = useQueryClient();
  return useMutation<RespuestaApi<ColadaDetalle>, Error, { id: number } & AsignarObraRequest>({
    mutationFn: ({ id, ...body }) => cliente.coladas.asignarObra(id, body as AsignarObraRequest),
    onSuccess: (respuesta, vars) => {
      if (respuesta.data) {
        toast.success(
          vars.obra_works_no === null
            ? 'Obra quitada de la colada'
            : `Obra ${vars.obra_works_no} asignada a la colada`,
        );
        qc.setQueryData(['colada', vars.id], respuesta);
        qc.invalidateQueries({ queryKey: ['coladas'] });
      } else {
        toast.error(respuesta.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => {
      toast.error('No se pudo asignar la obra', { description: e.message });
    },
  });
}
